import { Controller } from "@hotwired/stimulus"

/**
 * Unified Background Task Controller
 * 
 * Provides consistent UX for all background tasks:
 * - Button state management (idle -> processing -> completed/failed)
 * - Auto-refresh of progress partials
 * - Progress bar updates
 * - Status badge updates
 * 
 * Usage:
 *   <div data-controller="background-task"
 *        data-background-task-task-type-value="competitive_intelligence"
 *        data-background-task-task-id-value="123"
 *        data-background-task-status-url-value="/companies/slug/tasks/status">
 *     <button data-background-task-target="button"
 *             data-action="click->background-task#startTask">Start</button>
 *     <div data-background-task-target="progressContainer"></div>
 *   </div>
 */
export default class extends Controller {
  static targets = [
    "button",           // The button that triggers the task
    "progressContainer", // Container for progress partial (auto-refreshed)
    "statusBadge",     // Status badge element
    "progressBar",     // Progress bar element
    "progressText",    // Progress percentage text
    "phaseText"        // Current phase/step text
  ]
  
  static values = {
    taskType: String,        // Type of task (competitive_intelligence, publishing_bundle, etc.)
    taskId: String,          // ID of the task (report_id, bundle_id, etc.)
    statusUrl: String,       // URL to fetch status (e.g., "/companies/:slug/reports/workflow_status")
    startUrl: String,        // URL to start the task (e.g., "/companies/:slug/reports/search_intelligence")
    pollingInterval: { type: Number, default: 3000 }, // Polling interval in ms
    autoStart: { type: Boolean, default: false }      // Auto-start polling if task is in progress
  }

  connect() {
    this.isStarting = false
    this.pollingTimer = null
    this.originalButtonContent = null
    
    // Ensure autoStart is a boolean (Stimulus might pass it as string)
    const shouldAutoStart = this.autoStartValue === true || this.autoStartValue === 'true'
    
    console.log('BackgroundTaskController: Connected', {
      taskType: this.taskTypeValue,
      taskId: this.taskIdValue,
      autoStartRaw: this.autoStartValue,
      autoStartParsed: shouldAutoStart,
      statusUrl: this.statusUrlValue,
      hasProgressContainer: this.hasProgressContainerTarget
    })
    
    // Clean up any stray %> from Rails development annotations in initial render
    if (this.hasProgressContainerTarget) {
      const container = this.progressContainerTarget
      // Remove Rails development annotations and stray %>
      container.innerHTML = container.innerHTML
        .replace(/<!-- BEGIN[^>]*-->/g, '')
        .replace(/<!-- END[^>]*-->/g, '')
        .replace(/%>\s*$/gm, '')
        .replace(/<!-- BEGIN[^>]*-->%>/g, '')
    }
    
    // Auto-start polling if task is already in progress
    if (shouldAutoStart && this.taskIdValue && this.statusUrlValue) {
      console.log('BackgroundTaskController: Auto-starting polling', {
        autoStart: shouldAutoStart,
        taskId: this.taskIdValue,
        statusUrl: this.statusUrlValue
      })
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        this.startPolling()
      }, 100)
    } else {
      console.log('BackgroundTaskController: Not auto-starting polling', {
        shouldAutoStart,
        hasTaskId: !!this.taskIdValue,
        hasStatusUrl: !!this.statusUrlValue
      })
    }
  }

  disconnect() {
    this.stopPolling()
  }

  /**
   * Start the background task
   */
  async startTask(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    // Prevent double-firing
    if (this.isStarting) {
      return
    }
    this.isStarting = true

    const button = this.hasButtonTarget ? this.buttonTarget : event?.target?.closest('button')
    if (!button) {
      console.error('BackgroundTaskController: Could not find button element')
      this.isStarting = false
      return
    }

    // Store original button content
    this.originalButtonContent = button.innerHTML

    // Update button to processing state
    this.setButtonState(button, 'processing')

    // Show progress container if it exists
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.remove('hidden')
    }

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      if (!csrfToken) {
        throw new Error('CSRF token not found')
      }

      const response = await fetch(this.startUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify(this.getStartPayload())
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP error! status: ${response.status}` }))
        
        // Handle validation errors
        if (response.status === 422 && errorData.status === 'validation_error') {
          this.handleValidationErrors(errorData.errors, errorData.message)
          this.setButtonState(button, 'idle', this.originalButtonContent)
          this.isStarting = false
          return
        }
        
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      // Extract task ID from response
      // For content assets, use the content_asset id
      if (data.workflow_id || data.report_id || data.task_id || data.id) {
        const newTaskId = data.workflow_id || data.report_id || data.task_id || data.id
        this.taskIdValue = newTaskId
        
        // For content assets, if we have a redirect_url, extract the slug from it
        // and update the status URL to use that slug (since routes use :slug param)
        // This ensures we're polling the correct content asset even if the slug changed
        if (data.redirect_url && this.statusUrlValue && this.statusUrlValue.includes('/content/')) {
          // Extract slug from redirect_url (format: /content/:slug)
          const slugMatch = data.redirect_url.match(/\/content\/([^\/\?]+)/)
          if (slugMatch && slugMatch[1]) {
            const slug = slugMatch[1]
            // Update status URL to use the slug from redirect_url
            // Handle both UUID and slug formats
            const oldStatusUrl = this.statusUrlValue
            this.statusUrlValue = this.statusUrlValue.replace(/\/content\/[^\/]+\/(brief_status|draft_status|review_status)/, `/content/${slug}/$1`)
            console.log('BackgroundTaskController: Updated status URL with slug from redirect', {
              redirectUrl: data.redirect_url,
              slug: slug,
              oldStatusUrl: oldStatusUrl,
              updatedStatusUrl: this.statusUrlValue,
              taskId: newTaskId
            })
          } else {
            console.warn('BackgroundTaskController: Could not extract slug from redirect_url', {
              redirectUrl: data.redirect_url,
              statusUrl: this.statusUrlValue
            })
          }
        }
        
        this.updateStatus('pending', 0, 'Task started successfully')
        this.startPolling()
      } else if (data.success && data.redirect_url) {
        // For synchronous operations that complete immediately, redirect
        // This handles cases where generation completes synchronously
        window.location.href = data.redirect_url
        return
      } else {
        throw new Error('Failed to start task - no task ID returned')
      }

      this.isStarting = false
    } catch (error) {
      console.error('Error starting task:', error)
      this.showError(error.message || 'Failed to start task')
      this.setButtonState(button, 'idle', this.originalButtonContent)
      this.isStarting = false
    }
  }

  /**
   * Start polling for task status
   */
  startPolling() {
    // Prevent multiple polling timers
    if (this.pollingTimer) {
      console.log('BackgroundTaskController: Polling already active, skipping start')
      return
    }

    if (!this.taskIdValue) {
      console.warn('BackgroundTaskController: Cannot start polling without task ID')
      return
    }

    if (!this.statusUrlValue) {
      console.warn('BackgroundTaskController: Cannot start polling without status URL')
      return
    }

    console.log('BackgroundTaskController: Starting polling', {
      taskId: this.taskIdValue,
      statusUrl: this.statusUrlValue,
      interval: this.pollingIntervalValue
    })

    // Check immediately
    this.checkStatus()

    // Then poll at intervals
    this.pollingTimer = setInterval(() => {
      this.checkStatus()
    }, this.pollingIntervalValue)
  }

  /**
   * Stop polling
   */
  stopPolling() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer)
      this.pollingTimer = null
    }
  }

  /**
   * Check task status
   */
  async checkStatus() {
    if (!this.taskIdValue || !this.statusUrlValue) {
      console.warn('BackgroundTaskController: Cannot check status - missing taskId or statusUrl', {
        taskId: this.taskIdValue,
        statusUrl: this.statusUrlValue
      })
      return
    }

    try {
      const url = this.buildStatusUrl()
      console.log('BackgroundTaskController: Checking status', { url, taskId: this.taskIdValue })
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        // Handle 404 (not found) - stop polling immediately
        if (response.status === 404) {
          const errorData = await response.json().catch(() => ({ error: 'Content asset not found' }))
          console.error('BackgroundTaskController: Content asset not found (404), stopping polling', {
            url: this.buildStatusUrl(),
            taskId: this.taskIdValue,
            error: errorData.error || errorData.message
          })
          this.stopPolling()
          this.handleFailed(errorData.error || errorData.message || 'Content asset not found. It may have been deleted.')
          return
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      
      // Reset failure counter on successful response
      this.consecutiveFailures = 0
      
      console.log('BackgroundTaskController: Status check response', {
        status: data.status,
        normalizedStatus: (data.status || '').toLowerCase(),
        progress: data.progress,
        taskId: this.taskIdValue,
        isCompleted: (data.status || '').toLowerCase() === 'completed',
        isFailed: (data.status || '').toLowerCase() === 'failed',
        fullResponse: data
      })

      // Check for completion - be more flexible with status values
      // Normalize status to lowercase for comparison
      const normalizedStatus = (data.status || '').toLowerCase()
      const isCompleted = normalizedStatus === 'completed' || 
                         (data.progress === 100 && normalizedStatus !== 'failed')
      
      const isFailed = normalizedStatus === 'failed' || 
                       normalizedStatus === 'error' ||
                       (data.error_message || data.error)

      if (isCompleted) {
        console.log('BackgroundTaskController: Task completed, handling completion', data)
        this.handleComplete(data)
      } else if (isFailed) {
        console.log('BackgroundTaskController: Task failed, handling failure', data)
        this.handleFailed(data.error_message || data.error || 'Task failed')
      } else {
        // Refresh progress partial if URL provided (this replaces the entire container)
        // Don't call updateStatus separately as it might conflict with the partial refresh
        if (data.progress_partial_url) {
          this.refreshProgressPartial(data.progress_partial_url)
        } else {
          // Fallback: Update status directly if no partial URL
          this.updateStatus(
            data.status,
            data.progress || 0,
            data.message || this.getStatusMessage(data.status),
            data.phase_name || data.phase || null
          )
        }
      }
    } catch (error) {
      console.error('BackgroundTaskController: Error checking status', {
        error: error.message,
        url: this.buildStatusUrl(),
        taskId: this.taskIdValue
      })
      // Track consecutive failures and stop polling after 3 failures
      this.consecutiveFailures = (this.consecutiveFailures || 0) + 1
      if (this.consecutiveFailures >= 3) {
        console.error('BackgroundTaskController: Too many consecutive failures, stopping polling')
        this.stopPolling()
        this.handleFailed('Unable to check task status. Please refresh the page.')
      }
    }
  }

  /**
   * Update status display
   */
  updateStatus(status, progress, message, phaseName = null) {
    // Update status badge
    if (this.hasStatusBadgeTarget) {
      this.statusBadgeTarget.textContent = this.humanize(status)
      this.statusBadgeTarget.className = this.getStatusBadgeClass(status)
    }

    // Update progress bar
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${progress}%`
    }

    // Update progress text
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `${progress}%`
    }

    // Update phase text
    if (this.hasPhaseTextTarget && phaseName) {
      this.phaseTextTarget.textContent = phaseName
    }

    // Update progress container if it has a message element
    if (this.hasProgressContainerTarget) {
      const messageEl = this.progressContainerTarget.querySelector('[data-message]')
      if (messageEl) {
        messageEl.textContent = message
      }
    }
  }

  /**
   * Handle task completion
   */
  handleComplete(data) {
    console.log('BackgroundTaskController: Task completed', data)
    this.stopPolling()
    this.updateStatus('completed', 100, 'Task completed successfully!')

    // Update button to completed state
    if (this.hasButtonTarget) {
      this.setButtonState(this.buttonTarget, 'completed')
    }

    // Update progress container to show completed state immediately
    if (this.hasProgressContainerTarget) {
      // Show completed message immediately
      this.progressContainerTarget.innerHTML = `
        <div class="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-green-800">Task completed successfully!</h3>
              <p class="mt-1 text-sm text-green-700">Results are ready to view.</p>
            </div>
          </div>
        </div>
      `
    }

    // Refresh progress partial if URL provided (to get latest data)
    if (data.progress_partial_url) {
      this.refreshProgressPartial(data.progress_partial_url).catch((error) => {
        console.warn('BackgroundTaskController: Could not refresh progress partial', error)
      })
    }

    // Reload page after a short delay to show final results
    setTimeout(() => {
      if (data.redirect_url) {
        window.location.href = data.redirect_url
      } else {
        window.location.reload()
      }
    }, 1000)
  }

  /**
   * Handle task failure
   */
  handleFailed(errorMessage) {
    this.stopPolling()
    this.updateStatus('failed', 0, 'Task failed')
    this.showError(errorMessage)

    // Update button to failed state
    if (this.hasButtonTarget) {
      this.setButtonState(this.buttonTarget, 'failed', this.originalButtonContent)
    }
  }

  /**
   * Set button state
   */
  setButtonState(button, state, content = null) {
    button.disabled = (state === 'processing' || state === 'completed')
    
    switch (state) {
      case 'processing':
        button.innerHTML = `
          <svg class="animate-spin -ml-1 mr-2 h-4 w-4 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Processing...
        `
        button.classList.add('opacity-50', 'cursor-not-allowed')
        break
      case 'completed':
        button.innerHTML = `
          <svg class="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
          Completed
        `
        button.classList.add('opacity-50', 'cursor-not-allowed')
        break
      case 'failed':
        button.innerHTML = content || this.originalButtonContent || 'Retry'
        button.classList.remove('opacity-50', 'cursor-not-allowed')
        break
      case 'idle':
      default:
        button.innerHTML = content || this.originalButtonContent || 'Start'
        button.classList.remove('opacity-50', 'cursor-not-allowed')
        break
    }
  }

  /**
   * Refresh progress partial via Turbo
   */
  async refreshProgressPartial(url) {
    if (!this.hasProgressContainerTarget) {
      return Promise.resolve()
    }

    try {
      console.log('BackgroundTaskController: Refreshing progress partial', url)
      const response = await fetch(url, {
        headers: {
          'Accept': 'text/vnd.turbo-stream.html, text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (response.ok) {
        let html = await response.text()
        
        // Remove Rails development annotations that might cause issues
        html = html.replace(/<!-- BEGIN[^>]*-->/g, '')
        html = html.replace(/<!-- END[^>]*-->/g, '')
        // Remove any stray %> that might appear from ERB rendering issues (including after BEGIN comments)
        html = html.replace(/%>\s*$/gm, '')
        html = html.replace(/<!-- BEGIN[^>]*-->%>/g, '')
        
        // Always use direct DOM replacement to avoid Turbo Stream appending issues
        // This ensures we replace the content, not append to it
        this.progressContainerTarget.innerHTML = html
        console.log('BackgroundTaskController: Progress partial refreshed (replaced)')
        return Promise.resolve()
      } else {
        return Promise.reject(new Error(`HTTP ${response.status}`))
      }
    } catch (error) {
      console.error('BackgroundTaskController: Error refreshing progress partial', error)
      return Promise.reject(error)
    }
  }

  /**
   * Build status URL with task ID
   */
  buildStatusUrl() {
    let url = this.statusUrlValue
    
    // For content assets routes (/content/:slug/brief_status), the URL should already have the slug
    // But if we have a task ID and the URL contains a UUID that doesn't match, we might need to update it
    // However, content asset routes use slugs, not IDs, so we should use the slug from the redirect_url if available
    // For now, just use the URL as-is since it should already be correct from the view
    
    // Replace placeholders with actual task ID (for routes that use placeholders)
    url = url.replace(':task_id', this.taskIdValue)
    url = url.replace(':id', this.taskIdValue)
    url = url.replace(':workflow_id', this.taskIdValue)
    url = url.replace(':report_id', this.taskIdValue)
    
    // For member routes (like /social_media_generators/:id/status), Rails path helpers
    // already include the ID in the path, so we don't need to add it as a query param
    // Only add query params if the URL still has placeholders (collection routes)
    if (url.includes(':')) {
      // Still has placeholders - this shouldn't happen, but log it
      console.warn('BackgroundTaskController: URL still has placeholders after replacement', url)
    }
    
    // Don't add query params for member routes - the ID is already in the path
    // Only collection routes (like /companies/:slug/reports/workflow_status) need query params
    const isCollectionRoute = url.includes('/workflow_status') || url.includes('/status?') || (url.includes('/status') && !url.match(/\/[^\/]+\/status$/))
    
    if (isCollectionRoute && !url.includes('?')) {
      // Collection route without query params - add the task ID
      url += `?workflow_id=${this.taskIdValue}`
    } else if (isCollectionRoute && url.includes('?') && !url.includes('workflow_id=') && !url.includes('task_id=')) {
      // Collection route with existing query params - append task ID
      url += `&workflow_id=${this.taskIdValue}`
    }
    
    console.log('BackgroundTaskController: Built status URL', {
      original: this.statusUrlValue,
      built: url,
      taskId: this.taskIdValue,
      isCollectionRoute
    })
    
    return url
  }

  /**
   * Get payload for start request
   */
  getStartPayload() {
    // Default payload - can be overridden via data attributes or subclasses
    const payload = {}
    
    // For competitive intelligence, include search_query
    if (this.taskTypeValue === 'competitive_intelligence') {
      payload.search_query = "Market intelligence analysis"
    }
    
    // Collect form fields from the same container (for forms with select fields, etc.)
    const formFields = this.element.querySelectorAll('[data-background-task-target="formField"], input, select, textarea')
    formFields.forEach(field => {
      if (!field.name) return
      if (field.type === 'checkbox') {
        if (field.checked) payload[field.name] = field.value || '1'
      } else if (field.tagName === 'SELECT' || field.tagName === 'TEXTAREA') {
        // Always include select/textarea so server receives the key; value may be empty
        payload[field.name] = field.value != null ? field.value : ''
      } else if (field.value) {
        payload[field.name] = field.value
      }
    })
    
    return payload
  }

  /**
   * Get status message for status
   */
  getStatusMessage(status) {
    const messages = {
      'pending': 'Preparing task...',
      'processing': 'Processing...',
      'gathering': 'Gathering data...',
      'analyzing': 'Analyzing...',
      'researching': 'Researching...',
      'generating': 'Generating content...',
      'completed': 'Completed!',
      'failed': 'Task failed'
    }
    return messages[status] || 'Processing...'
  }

  /**
   * Get status badge CSS class
   */
  getStatusBadgeClass(status) {
    const baseClasses = "inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
    const statusClasses = {
      "pending": "bg-gray-100 text-gray-800",
      "processing": "bg-blue-100 text-blue-800",
      "gathering": "bg-blue-100 text-blue-800",
      "analyzing": "bg-yellow-100 text-yellow-800",
      "researching": "bg-blue-100 text-blue-800",
      "generating": "bg-purple-100 text-purple-800",
      "completed": "bg-green-100 text-green-800",
      "failed": "bg-red-100 text-red-800",
      "reviewing": "bg-yellow-100 text-yellow-800"
    }
    return `${baseClasses} ${statusClasses[status] || "bg-gray-100 text-gray-800"}`
  }

  /**
   * Humanize string
   */
  humanize(str) {
    if (!str) return ""
    return str
      .replace(/_/g, " ")
      .replace(/\b\w/g, l => l.toUpperCase())
  }

  /**
   * Show error message
   */
  showError(message) {
    // Create or update error display
    let errorContainer = document.getElementById('background-task-errors')
    if (!errorContainer) {
      errorContainer = document.createElement('div')
      errorContainer.id = 'background-task-errors'
      errorContainer.className = 'fixed top-4 right-4 z-50 max-w-md'
      document.body.appendChild(errorContainer)
    }

    const errorDiv = document.createElement('div')
    errorDiv.className = 'bg-red-50 border border-red-200 rounded-md p-4 mb-4'
    errorDiv.innerHTML = `
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-sm font-medium text-red-800">${this.escapeHtml(message)}</p>
        </div>
      </div>
    `

    errorContainer.appendChild(errorDiv)

    // Auto-remove after 10 seconds
    setTimeout(() => {
      if (errorDiv.parentElement) {
        errorDiv.remove()
      }
    }, 10000)
  }

  /**
   * Handle validation errors
   */
  handleValidationErrors(errors, message) {
    // Override in subclasses for custom validation error handling
    this.showError(message || 'Please fix the validation errors before continuing')
  }

  /**
   * Escape HTML
   */
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
